import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";

import { AuthSession } from "@aws-amplify/core";
import { AuthFlowType } from "@aws-amplify/auth/dist/esm/providers/cognito/types/models";
import { SetUpTOTPOutput } from "@aws-amplify/auth";
import { Outlet, useNavigate } from "react-router-dom";
import { isArray } from "lodash";

import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession as amplifyFetchAuthSession,
  resetPassword as amplifyResetPassword,
  fetchMFAPreference as amplifyFetchMFAPreference,
  setUpTOTP as amplifySetupTOTP,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updateMFAPreference as amplifyUpdateMFAPreference,
  verifyTOTPSetup as amplifyVerifyTOTPSetup,
} from "aws-amplify/auth";
import useAsyncEffect from "use-async-effect";

import { Toast } from "@covertree/utils";

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const noop: any = () => {};

import {
  BasicUserDataFromToken,
  CreateNewPasswordInput,
  LoginInput,
} from "../types";

interface AuthProviderState {
  isSignedIn: boolean;
  basicUserData?: BasicUserDataFromToken;
  tokenPayload: Record<string, any>;
  login(input: LoginInput): Promise<void>;
  signOut(): Promise<void>;
  createNewPassword(input: CreateNewPasswordInput): Promise<void>;
  confirmSignIn(code: string): Promise<void>;
  setupTOTP(): Promise<string | undefined>;
  verifyTOTP(userCode: string): Promise<void>;
  forgotPassword(email: string): Promise<void>;
  resendForgotPasswordCode(email: string): Promise<void>;
  resetPassword(email: string, code: string, password: string): Promise<void>;
  hasGroup(groupOrGroups: string | string[]): boolean;
}

const initialState: AuthProviderState = {
  isSignedIn: false,
  basicUserData: undefined,
  tokenPayload: {},
  login: noop,
  signOut: noop,
  createNewPassword: noop,
  confirmSignIn: noop,
  setupTOTP: noop,
  verifyTOTP: noop,
  forgotPassword: noop,
  resendForgotPasswordCode: noop,
  resetPassword: noop,
  hasGroup: noop,
};

const AuthContext = createContext<AuthProviderState>(initialState);

export const useAuthContext = () => useContext(AuthContext);

interface AuthProviderProps extends PropsWithChildren {
  authFlowType?: AuthFlowType;
  allowedGroups?: string[];
  confirmSignInPath: string;
  createNewPasswordPath?: string;
  createNewPasswordSuccessPath?: string;
  environment?: string;
  mfaSetupPath?: string;
  onSignIn?(basicUserData: BasicUserDataFromToken): void;
  onSignOut?(): void;
}

export const AuthProvider = ({
  authFlowType = "USER_SRP_AUTH",
  allowedGroups,
  children,
  confirmSignInPath,
  createNewPasswordPath,
  createNewPasswordSuccessPath,
  environment,
  mfaSetupPath,
  onSignIn,
  onSignOut,
}: AuthProviderProps) => {
  const navigate = useNavigate();

  const [totpSetup, setTotpSetup] = useState<SetUpTOTPOutput | undefined>();

  const [tokenPayload, setTokenPayload] = useState(initialState.tokenPayload);
  const [basicUserData, setBasicUserData] = useState<
    BasicUserDataFromToken | undefined
  >(initialState.basicUserData);

  const [isSignedIn, setIsSignedIn] = useState(initialState.isSignedIn);
  const [isUserCheckInProgress, setIsUserCheckInProgress] = useState(true);

  const login = useCallback(
    async ({ username, password }: LoginInput) => {
      const resp = await amplifySignIn({
        username: username.toLowerCase(),
        ...(!!password ? { password } : {}),
        options: {
          authFlowType,
        },
      });

      switch (resp.nextStep.signInStep) {
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
          navigate(mfaSetupPath!);
          break;
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        case "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE":
          navigate(confirmSignInPath, {
            state: {
              challenge: resp.nextStep.signInStep,
              username,
              password,
            },
          });
          break;
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
          navigate(createNewPasswordPath!, {
            state: { username },
          });
          break;
        default:
          if (mfaSetupPath) {
            navigate(mfaSetupPath);
            break;
          }
          const { basicUserData: freshBasicUserData } =
            await refreshAuthToken();
          onSignIn?.(freshBasicUserData!);
          navigate("/", { replace: true });
          break;
      }
    },
    [confirmSignInPath, createNewPasswordPath, navigate]
  );

  const signOut = useCallback(async () => {
    await amplifySignOut();

    onSignOut?.();

    setIsSignedIn(initialState.isSignedIn);
    setBasicUserData(initialState.basicUserData);
    setTokenPayload(initialState.tokenPayload);

    navigate("/", { replace: true });
  }, [onSignOut]);

  const createNewPassword = useCallback(
    async ({ password }: CreateNewPasswordInput) => {
      try {
        await amplifyConfirmSignIn({
          challengeResponse: password,
        });
        await signOut();

        navigate(createNewPasswordSuccessPath as any);
      } catch (error) {
        Toast.showError(error);
      }
    },
    [createNewPasswordSuccessPath, navigate, signOut]
  );

  const confirmSignIn = useCallback(async (code: string) => {
    await amplifyConfirmSignIn({
      challengeResponse: code,
    });

    const { basicUserData: freshBasicUserData } = await refreshAuthToken();

    onSignIn?.(freshBasicUserData!);

    navigate("/", { replace: true });
  }, []);

  const setupTOTP = useCallback(async () => {
    const authSession = await amplifyFetchAuthSession({
      forceRefresh: true,
    });
    const {
      email,
      phone_number,
      "cognito:groups": groups,
    } = authSession!.tokens!.idToken!.payload;

    const username = email ?? phone_number;

    if (
      allowedGroups &&
      !(groups as string[]).some((group) => allowedGroups.includes(group))
    ) {
      await signOut();
      Toast.showError("You are not authorized to access this application");
      navigate("/", { replace: true });
      return;
    }

    const totpSetupFinalized =
      totpSetup ??
      (await (async () => {
        const setup = await amplifySetupTOTP();
        setTotpSetup(setup);
        return setup;
      })());

    const envSuffix = environment === "prod" ? "" : `_${environment}`;

    return `otpauth://totp/CoverTree%20Admin%20Portal${envSuffix}:${username}?secret=${totpSetupFinalized.sharedSecret}&issuer=CoverTree`;
  }, [totpSetup, tokenPayload]);

  const verifyTOTP = useCallback(async (code: string) => {
    try {
      await amplifyVerifyTOTPSetup({
        code,
      });
      await amplifyUpdateMFAPreference({
        sms: "DISABLED",
        totp: "PREFERRED",
      });

      const { basicUserData: freshBasicUserData } = await refreshAuthToken();

      onSignIn?.(freshBasicUserData!);

      setTotpSetup(undefined);

      navigate("/", { replace: true });
    } catch (e) {
      Toast.showError(e);
    }
  }, []);

  const forgotPassword = useCallback(
    async (username: string) => {
      try {
        await amplifyResetPassword({
          username: username.toLowerCase(),
        });

        navigate(createNewPasswordPath!, {
          state: { isPasswordReset: true, username },
        });
      } catch (error) {
        Toast.showError(error);
      }
    },
    [createNewPasswordPath, navigate]
  );

  const resendForgotPasswordCode = useCallback(async (email: string) => {
    await amplifyResetPassword({
      username: email.toLowerCase(),
    });
  }, []);

  const resetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      try {
        await amplifyConfirmResetPassword({
          username: email.toLowerCase(),
          newPassword,
          confirmationCode: code,
        });

        navigate(createNewPasswordSuccessPath!);
      } catch (error) {
        Toast.showError(error);
      }
    },
    [createNewPasswordSuccessPath, navigate]
  );

  const refreshAuthToken = useCallback(async (session?: AuthSession) => {
    const authSession =
      session ??
      (await amplifyFetchAuthSession({
        forceRefresh: true,
      }));

    const authToken = authSession.tokens?.idToken?.toString();

    let basicUserData: BasicUserDataFromToken | undefined = undefined;

    if (!authToken) {
      setIsSignedIn(initialState.isSignedIn);
      setBasicUserData(initialState.basicUserData);
      setTokenPayload(initialState.tokenPayload);
    } else {
      const firstName = (authSession.tokens?.idToken?.payload["given_name"] ??
        "") as string;
      const lastName = (authSession.tokens?.idToken?.payload["family_name"] ??
        "") as string;
      const email = (authSession.tokens?.idToken?.payload["email"] ??
        "") as string;
      const groups = (authSession.tokens?.idToken?.payload["cognito:groups"] ??
        []) as string[];
      const sub = (authSession.tokens?.idToken?.payload["sub"] ?? "") as string;
      const agentId = (authSession.tokens?.idToken?.payload["agentId"] ??
        "") as string;
      const agentDateOfBirth = (authSession.tokens?.idToken?.payload[
        "agentDateOfBirth"
      ] ?? "") as string;
      const agencyId = (authSession.tokens?.idToken?.payload["agencyId"] ??
        "") as string;
      const agencyName = (authSession.tokens?.idToken?.payload["agencyName"] ??
        "") as string;
      const agencyType = (authSession.tokens?.idToken?.payload["agencyType"] ??
        "") as string;
      const wholesalerId = (authSession.tokens?.idToken?.payload[
        "wholesalerId"
      ] ?? "") as string;

      basicUserData = {
        firstName,
        lastName,
        email,
        groups,
        sub,
        agentId,
        agentDateOfBirth,
        agencyId,
        agencyName,
        agencyType,
        wholesalerId,
      };

      setIsSignedIn(true);
      setBasicUserData(basicUserData);
      setTokenPayload(authSession.tokens?.idToken?.payload as any);
    }

    return { authToken, basicUserData };
  }, []);

  const hasGroup = useCallback(
    (groupOrGroups: string | string[]) => {
      if (isArray(groupOrGroups)) {
        return groupOrGroups.some((group) =>
          (basicUserData?.groups ?? []).includes(group)
        );
      }

      return (basicUserData?.groups ?? []).includes(groupOrGroups);
    },
    [basicUserData]
  );

  useAsyncEffect(async () => {
    try {
      const session = await amplifyFetchAuthSession();

      if (!session.tokens) {
        return;
      }

      if (allowedGroups) {
        if (
          !(
            (session.tokens?.idToken?.payload["cognito:groups"] as string[]) ??
            []
          ).some((group) => allowedGroups.includes(group))
        ) {
          await signOut();
          navigate("/", { replace: true });
          Toast.showError("You are not authorized to access this application");
          return;
        }
      }

      if (mfaSetupPath) {
        const mfaPreference = await amplifyFetchMFAPreference();
        if (!mfaPreference.enabled) {
          navigate(mfaSetupPath);
          return;
        }
      }

      const firstName = (session.tokens.idToken?.payload["given_name"] ??
        "") as string;
      const lastName = (session.tokens.idToken?.payload["family_name"] ??
        "") as string;
      const email = (session.tokens.idToken?.payload["email"] ?? "") as string;
      const groups = (session.tokens.idToken?.payload["cognito:groups"] ??
        []) as string[];
      const sub = (session.tokens.idToken?.payload["sub"] ?? "") as string;
      const agentId = (session.tokens.idToken?.payload["agentId"] ??
        "") as string;
      const agentDateOfBirth = (session.tokens.idToken?.payload[
        "agentDateOfBirth"
      ] ?? "") as string;
      const agencyId = (session.tokens.idToken?.payload["agencyId"] ??
        "") as string;
      const agencyName = (session.tokens.idToken?.payload["agencyName"] ??
        "") as string;
      const agencyType = (session.tokens.idToken?.payload["agencyType"] ??
        "") as string;
      const wholesalerId = (session.tokens.idToken?.payload["wholesalerId"] ??
        "") as string;

      const basicUserData = {
        firstName,
        lastName,
        email,
        groups,
        sub,
        agentId,
        agentDateOfBirth,
        agencyId,
        agencyName,
        agencyType,
        wholesalerId,
      };

      setIsSignedIn(true);
      setBasicUserData(basicUserData);
      setTokenPayload(session.tokens?.idToken?.payload as any);
      onSignIn?.(basicUserData);
    } catch (e) {
      // We don't want to handle this case
    } finally {
      setIsUserCheckInProgress(false);
    }
  }, []);

  if (isUserCheckInProgress) return null;

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        tokenPayload,
        basicUserData,
        login,
        signOut,
        createNewPassword,
        confirmSignIn,
        setupTOTP,
        verifyTOTP,
        forgotPassword,
        resendForgotPasswordCode,
        resetPassword,
        hasGroup,
      }}>
      {!!children ? children : <Outlet />}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
